
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";
import UserSite from "@/store/userSite";
import {
  FETCH_ALL_SITES,
  GET_ALL_SITES,
  GET_SELECTED_SITES,
  SITE_STORE,
  USER_SELECTED_SITES,
  RESET_USER_SELECTED_SITES,
} from "@/store/userSite/types";
import { IDataTableFilterOption, IOptionModel } from "@/interfaces/iPageModel";
import VZFilter from "@/components/kt-datatable/components/VZFilter.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { getLookups } from "@/services/api/Lookup.service";
import { Actions } from "@/store/enums/StoreEnums";
export default defineComponent({
  name: "vz-global-filter",
  components: { VZFilter },
  setup() {
    const visibleCounter = ref<number>(0);
    const MAX_WIDTH = 30;
    const siteAlsoSelected = ref<boolean>(false);
    const city = ref<any>([]);
    const selectedPostCode = ref<any>([]);
    const state = ref<any>([]);
    const cityNames = ref<any>([]);
    const postalCodeNames = ref<any>([]);

    const stateNames = ref<any>([]);
    const postalCode = ref("");
    const store = useStore();
    const stateOptions = ref<{ label: string; value: string }[]>([]);
    const cityOptions = ref<{ label: string; value: string }[]>([]);
    const postalCodeOptions = ref<{ label: string; value: string }[]>([]);
    const globalFilter = ref<any>();
    const siteOptionsCopy = ref<any>();
    const isInitialLoad = ref<boolean>(true);
    const hasDefaultFilters = ref<boolean>(false);
    const filtersReady = ref<boolean>(false);

    getModule(UserSite);

    const sitesOptions = computed(() => {
      const key = `${SITE_STORE}/${GET_ALL_SITES}`;
      return store.getters[key] as Array<IOptionModel>;
    });
    const selectedSites = computed(() => {
      const key = `${SITE_STORE}/${GET_SELECTED_SITES}`;
      return store.getters[key] as Array<IOptionModel>;
    });
    const chipText = computed(() => {
      getVisibleSites();
      return selectedSites.value.length > 1 &&
        selectedSites.value.length - 1 !== visibleCounter.value
        ? ` + ${selectedSites.value.length - visibleCounter.value - 1}`
        : "";
    });

    // Helper functions for lookups
    const getLookupCities = async (id) => {
      cityOptions.value = [];
      let res;
      if (id && id.length > 0) {
        res = await getLookups(
          `site/filter/SITE_CITIES?header=false&siteIds=${id}`
        );
      } else {
        res = await getLookups(
          `site/filter/SITE_CITIES?header=false&userId=123456`
        );
      }

      cityOptions.value = res.map((data) => ({
        label: data.text,
        value: data.value,
      }));
      let selectAll = { label: "Select All", value: "-1" };
      cityOptions.value.unshift(selectAll);

      if (tableFilter.value[1]) {
        tableFilter.value[1].options = cityOptions.value;
      }
    };

    const getLookupPostalCodes = async () => {
      postalCodeOptions.value = [];
      var res = [] as any;
      if (city.value.length > 0 || state.value.length > 0) {
        res = await getLookups(
          `site/filter/SITE_POSTAL_CODES?header=false&siteIds=${
            city.value.length > 0 ? city.value : state.value
          }`
        );
      } else {
        res = await getLookups(
          `site/filter/SITE_POSTAL_CODES?header=false&userId=123456`
        );
      }

      postalCodeOptions.value = res.map((data) => ({
        label: data.text,
        value: data.value,
      }));
      let selectAll = { label: "Select All", value: "-1" };
      postalCodeOptions.value.unshift(selectAll);

      if (tableFilter.value[2]) {
        tableFilter.value[2].options = postalCodeOptions.value;
      }
    };

    const loadSites = () => {
      const updatedUrl = [...tableFilter.value];
      let stateNameCopy = stateOptions.value
        .filter((item) => state.value.includes(item.value))
        .map((item) => item.label);
      let cityNameCopy = cityOptions.value
        .filter((item) => city.value.includes(item.value))
        .map((item) => item.label);
      let postalCodeNamesCopy = postalCodeOptions.value
        .filter((item) => selectedPostCode.value.includes(item.value))
        .map((item) => item.label);
      updatedUrl[3][
        "url"
      ] = `site/sites?header=false&status=ACTIVE&size=10000&city=${cityNameCopy}&state=${stateNameCopy}&postalCode=${postalCodeNamesCopy}`;
      tableFilter.value = [...updatedUrl];
    };

    // Optimized tableFilter
    const tableFilter = ref<Array<IDataTableFilterOption>>([
      {
        key: "state",
        label: TranslationService.translate("State"),
        type: "autocomplete",
        attrs: {
          mode: "tags",
        },
        textKey: "text",
        valueKey: "value",
        options: stateOptions.value,
        onChange: async (value) => {
          state.value = value;
          city.value = [];
          selectedPostCode.value = [];

          globalFilter.value.models.city = [];
          globalFilter.value.models.postalCode = [];
          globalFilter.value.models.siteId = [];

          await getLookupCities(state.value);
          await getLookupPostalCodes();
        },
      },
      {
        key: "city",
        label: TranslationService.translate("City"),
        type: "autocomplete",
        attrs: {
          mode: "tags",
        },
        textKey: "text",
        valueKey: "value",
        options: cityOptions.value,
        onChange: async (value) => {
          city.value = value;
          selectedPostCode.value = [];
          globalFilter.value.models.postalCode = [];
          globalFilter.value.models.siteId = [];
          await getLookupPostalCodes();
        },
      },
      {
        key: "postalCode",
        label: TranslationService.translate("Postal Code"),
        type: "autocomplete",
        attrs: {
          mode: "tags",
        },
        textKey: "text",
        valueKey: "value",
        options: postalCodeOptions.value,
        onChange: (value) => {
          selectedPostCode.value = value;
          globalFilter.value.models.siteId = [];
        },
      },
      {
        key: "siteId",
        label: TranslationService.translate("pages.site.site"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: `site/sites?header=false&status=ACTIVE&size=10000&city=${
          city.value && city.value.length > 0 ? city.value[0] : ""
        }&state=${
          stateNames.value && stateNames.value.length > 0
            ? stateNames.value
            : ""
        }&postalCode=${postalCode.value}`,
        payload: city.value,
        textKey: "content.name",
        valueKey: "content.webId",
      },
    ] as any);

    const setSelectedSite = async (filters) => {
      const {
        siteId,
        city: cityFilter,
        state: stateFilter,
        postalCode: postalCodeFilter,
      } = filters;

      // Check if this is initial load with default filters
      if (isInitialLoad.value && !filtersReady.value) {
        // Check if there are any default filters
        if (
          (cityFilter && cityFilter.length > 0) ||
          (stateFilter && stateFilter.length > 0) ||
          (postalCodeFilter && postalCodeFilter.length > 0) ||
          (siteId && siteId.length > 0)
        ) {
          hasDefaultFilters.value = true;
        }
      }

      // If all filters are empty, reset everything
      if (Object.keys(globalFilter.value.models).length === 0) {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        stateNames.value = [];
        cityNames.value = [];
        postalCodeNames.value = [];
        state.value = [];
        city.value = [];
        selectedPostCode.value = [];
        await store.dispatch(`${SITE_STORE}/${USER_SELECTED_SITES}`, []);
        await getLookupCities([]);
        await getLookupPostalCodes();
        loadSites();
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
        return;
      }

      store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");

      try {
        // Update state values from filters
        if (stateFilter !== undefined) {
          state.value = Array.isArray(stateFilter) ? stateFilter : [];
        }
        if (cityFilter !== undefined) {
          city.value = Array.isArray(cityFilter) ? cityFilter : [];
        }
        if (postalCodeFilter !== undefined) {
          selectedPostCode.value = Array.isArray(postalCodeFilter)
            ? postalCodeFilter
            : [];
        }

        // Update dependent lookups based on hierarchy
        if (state.value.length > 0) {
          await getLookupCities(state.value);
          await getLookupPostalCodes();
        } else if (city.value.length > 0) {
          if (isInitialLoad.value) {
            await getLookupCities([]);
          }
          await getLookupPostalCodes();
        } else if (selectedPostCode.value.length > 0) {
          if (isInitialLoad.value) {
            await getLookupCities([]);
            await getLookupPostalCodes();
          }
        }

        // Update display names AFTER lookups are loaded
        if (state.value.length > 0) {
          stateNames.value = stateOptions.value
            .filter((item) => state.value.includes(item.value))
            .map((item) => item.label);
        } else {
          stateNames.value = [];
        }

        if (city.value.length > 0) {
          cityNames.value = cityOptions.value
            .filter((item) => city.value.includes(item.value))
            .map((item) => item.label);
        } else {
          cityNames.value = [];
        }

        if (selectedPostCode.value.length > 0) {
          postalCodeNames.value = postalCodeOptions.value
            .filter((item) => selectedPostCode.value.includes(item.value))
            .map((item) => item.label);
        } else {
          postalCodeNames.value = [];
        }

        siteAlsoSelected.value = siteId && siteId.length > 0;

        // Update the sites URL with filters
        loadSites();

        // Mark filters as ready after first processing
        filtersReady.value = true;

        // Handle site selection
        if (
          siteId ||
          city.value.length > 0 ||
          state.value.length > 0 ||
          selectedPostCode.value.length > 0
        ) {
          let selection;

          if (siteId && siteId.length >= sitesOptions.value.length) {
            selection = sitesOptions.value;
          } else if (siteId && siteId.length > 0) {
            selection = sitesOptions.value.filter((site) =>
              siteId.includes(Number(site.value))
            );
          } else {
            // No specific sites selected, but location filters applied
            if (
              !stateNames.value.length &&
              !cityNames.value.length &&
              !postalCodeNames.value.length
            ) {
              selection = sitesOptions.value;
            } else {
              // Fetch sites based on location filters
              if (
                tableFilter.value &&
                Array.isArray(tableFilter.value) &&
                tableFilter.value[3]
              ) {
                await globalFilter.value?.getLookupData();
                if (tableFilter.value[3].options) {
                  selection = tableFilter.value[3].options.filter(
                    (site) => Number(site.value) && Number(site.value) != -1
                  );
                }
              }
            }
          }

          if (selection) {
            await store.dispatch(
              `${SITE_STORE}/${USER_SELECTED_SITES}`,
              selection
            );
          }
        }

        // Mark that initial load is complete
        isInitialLoad.value = false;
      } finally {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    };

    const getVisibleSites = () => {
      let index = 0;
      let counter = 0;
      if (!selectedSites.value.length) {
        return;
      }
      for (let i = 0; i < selectedSites.value.length; i++) {
        const text = selectedSites.value[i].text;
        if (!text || !text.length) {
          return;
        }
        if (text.length + counter < MAX_WIDTH) {
          counter = counter + text.length;
          index = i;
        } else {
          break;
        }
      }
      visibleCounter.value = index;
    };

    const removeSite = async (index) => {
      state.value = [];
      city.value = [];
      selectedPostCode.value = [];
      const selection =
        selectedSites.value?.filter(
          (site) => site.value !== selectedSites?.value[index].value
        ) ?? [];
      if (selection) {
        await store.dispatch(`${SITE_STORE}/${USER_SELECTED_SITES}`, selection);
        visibleCounter.value = visibleCounter.value - 1;
        let siteId = selection?.map((site) => Number(site.value));
        globalFilter.value.models = {};
        globalFilter.value.models.siteId = siteId;
        const data = {
          siteId: siteId,
          state: state.value,
          city: city.value,
          postalCode: selectedPostCode.value,
        };
        await globalFilter.value.resetFilter(data);
      }
      getLookupCities([]);
      getLookupPostalCodes();
    };

    const removeState = async (label) => {
      stateNames.value = stateNames.value.filter((item) => item != label);
      state.value = stateOptions.value
        .filter((obj) => stateNames.value.includes(obj.label))
        .map((obj) => obj.value);
      cityNames.value = [];
      postalCodeNames.value = [];
      city.value = [];
      selectedPostCode.value = [];
      loadSites();
      const data = {
        siteId: [],
        state: state.value,
        city: city.value,
        postalCode: selectedPostCode.value,
      };
      await globalFilter.value.resetFilter(data);
      getLookupCities([]);
      getLookupPostalCodes();
      visibleCounter.value = 0;
    };

    const removeCity = async (label) => {
      cityNames.value = cityNames.value.filter((item) => item != label);
      city.value = cityOptions.value
        .filter((obj) => cityNames.value.includes(obj.label))
        .map((obj) => obj.value);
      postalCodeNames.value = [];
      state.value = [];
      stateNames.value = [];
      selectedPostCode.value = [];
      loadSites();
      const data = {
        siteId: [],
        state: state.value,
        city: city.value,
        postalCode: selectedPostCode.value,
      };
      await globalFilter.value.resetFilter(data);
      getLookupCities([]);
      getLookupPostalCodes();
      visibleCounter.value = 0;
    };

    const removePostalCode = async (label) => {
      postalCodeNames.value = postalCodeNames.value.filter(
        (item) => item != label
      );
      selectedPostCode.value = postalCodeOptions.value
        .filter((obj) => postalCodeNames.value.includes(obj.label))
        .map((obj) => obj.value);
      state.value = [];
      city.value = [];
      stateNames.value = [];
      cityNames.value = [];
      loadSites();
      const data = {
        siteId: [],
        state: state.value,
        city: city.value,
        postalCode: selectedPostCode.value,
      };
      await globalFilter.value.resetFilter(data);
      getLookupCities([]);
      getLookupPostalCodes();
      visibleCounter.value = 0;
    };

    onMounted(async () => {
      // Check if default filters exist
      if (globalFilter.value?.models) {
        const models = globalFilter.value.models;
        if (
          models.city?.length > 0 ||
          models.state?.length > 0 ||
          models.postalCode?.length > 0 ||
          models.siteId?.length > 0
        ) {
          hasDefaultFilters.value = true;
        }
      }

      // Only fetch all sites if there are NO default filters
      if (!hasDefaultFilters.value) {
        await store.dispatch(`${SITE_STORE}/${FETCH_ALL_SITES}`);
      }

      let selectAll = { label: "Select All", value: "-1" };
      stateOptions.value.push(selectAll);

      await getLookups("site/filter/SITE_STATES")
        .then((res) => {
          res.map((data) => {
            stateOptions.value.push({
              label: data.text,
              value: data.value,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });

      siteOptionsCopy.value = [...sitesOptions.value];

      // Load initial lookups
      await getLookupCities([]);
      await getLookupPostalCodes();
    });

    watch(
      sitesOptions,
      () => {
        // Skip the initial watch trigger if we have default filters
        // and filters aren't ready yet
        if (hasDefaultFilters.value && !filtersReady.value) {
          return;
        }

        globalFilter.value?.getLookupData();
        if (selectedSites.value.length > 0) {
          const availableSites = sitesOptions.value?.filter((site) =>
            selectedSites.value.some((selectedSite) => {
              return selectedSite.value === site.value;
            })
          );
          setSelectedSite({
            siteId: availableSites?.map((site) => site.value),
          });
          const data = {
            siteId: availableSites.map((site) => site.value),
          };
          globalFilter.value.resetFilter(data);
        }
      },
      { deep: true }
    );

    return {
      sitesOptions,
      selectedSites,
      setSelectedSite,
      tableFilter,
      chipText,
      visibleCounter,
      globalFilter,
      removeSite,
      removeState,
      removeCity,
      removePostalCode,
      city,
      state,
      siteAlsoSelected,
      cityOptions,
      postalCodeOptions,
      stateOptions,
      cityNames,
      stateNames,
      getLookupPostalCodes,
      postalCodeNames,
      selectedPostCode,
      siteOptionsCopy,
    };
  },
});
